import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import type { LocaleCode } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { AccessNotice } from '../components';
import type { OperationsAccess } from '../operationsAccess';
import {
    ActivityDetailDrawer,
    ActivityFilters,
    ActivityTable,
} from './components';
import type { ActivityFilterState, ActivityRowModel } from './activityMeta';

type ActivityPageViewProps = {
    access: OperationsAccess;
    canRead: boolean;
    canReverse: boolean;
    locale: LocaleCode;
    isLoading: boolean;
    filters: ActivityFilterState;
    operations: { key: string; label: string }[];
    rows: ActivityRowModel[];
    pageInfo: {
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
    };
    selectedRecordId: string | null;
    onRangeChange: (from: string, to: string) => void;
    onKeyChange: (key: string) => void;
    onStatusChange: (status: string) => void;
    onPageChange: (page: number) => void;
    onSelect: (recordId: string) => void;
    onCloseDetail: () => void;
};

/**
 * The Activity tab — render only.
 *
 * Filters, a register, a pager, and a drawer over all three. The count above the
 * table is the **server's** `totalCount`, not the rendered row count: it is the
 * answer to "how many operations match this filter", which is a different
 * question from "how many are on this page".
 */
export const ActivityPageView: React.FC<ActivityPageViewProps> = ({
    access,
    canRead,
    canReverse,
    locale,
    isLoading,
    filters,
    operations,
    rows,
    pageInfo,
    selectedRecordId,
    onRangeChange,
    onKeyChange,
    onStatusChange,
    onPageChange,
    onSelect,
    onCloseDetail,
}) => {
    const { t } = useTranslation('pages');

    if (!canRead) {
        return (
            <section
                aria-label={t('operations.activity.title')}
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('operations-activity-section')}
            >
                <AccessNotice
                    access={
                        access as Exclude<OperationsAccess, { kind: 'full' }>
                    }
                />
            </section>
        );
    }

    return (
        <section
            aria-label={t('operations.activity.title')}
            className="flex flex-col gap-5"
            {...getNodeTestIdProp('operations-activity-section')}
        >
            <ActivityFilters
                filters={filters}
                isDisabled={isLoading}
                onKeyChange={onKeyChange}
                onRangeChange={onRangeChange}
                onStatusChange={onStatusChange}
                operations={operations}
            />

            <span
                className="text-sm text-muted"
                {...getNodeTestIdProp('operations-activity-count')}
            >
                {isLoading
                    ? t('operations.activity.countPending')
                    : t('operations.activity.count', {
                          count: pageInfo.totalCount,
                      })}
            </span>

            <ActivityTable
                isLoading={isLoading}
                locale={locale}
                onSelect={onSelect}
                rows={rows}
            />

            {pageInfo.totalPages > 1 ? (
                <div
                    className="flex flex-wrap items-center justify-between gap-3"
                    {...getNodeTestIdProp('operations-activity-pager')}
                >
                    <span className="text-sm text-muted">
                        {t('operations.activity.pageOf', {
                            page: pageInfo.page,
                            pages: pageInfo.totalPages,
                        })}
                    </span>
                    <span className="flex items-center gap-2">
                        <Button
                            isDisabled={isLoading || pageInfo.page <= 1}
                            onPress={() => onPageChange(pageInfo.page - 1)}
                            size="sm"
                            variant="outline"
                            {...getNodeTestIdProp(
                                'operations-activity-prev-page',
                            )}
                        >
                            <Icon aria-hidden icon="gravity-ui:chevron-left" />
                            {t('operations.activity.previous')}
                        </Button>
                        <Button
                            isDisabled={
                                isLoading ||
                                pageInfo.page >= pageInfo.totalPages
                            }
                            onPress={() => onPageChange(pageInfo.page + 1)}
                            size="sm"
                            variant="outline"
                            {...getNodeTestIdProp(
                                'operations-activity-next-page',
                            )}
                        >
                            {t('operations.activity.next')}
                            <Icon aria-hidden icon="gravity-ui:chevron-right" />
                        </Button>
                    </span>
                </div>
            ) : null}

            <ActivityDetailDrawer
                canReverse={canReverse}
                locale={locale}
                onClose={onCloseDetail}
                recordId={selectedRecordId}
            />
        </section>
    );
};
